import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Loader2, Flame, Sparkles, Heart, Eye, Users, Sprout, Zap, CheckCircle2,
  Compass, X, UserCheck, Award, ArrowRight, LineChart,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const PILLARS = [
  { key: "gratitude", label: "Gratitude", icon: Heart, color: "#D98E73" },
  { key: "awareness", label: "Awareness", icon: Eye, color: "#144F75" },
  { key: "unity", label: "Unity", icon: Users, color: "#8FA89B" },
  { key: "growth", label: "Growth", icon: Sprout, color: "#5B8A72" },
  { key: "energy", label: "Energy", icon: Zap, color: "#B5794E" },
];
const CATEGORIES = ["Self", "Mind", "Body", "Relationships", "Spirit", "Work"];
const pillarMeta = (k) => PILLARS.find((p) => p.key === k) || PILLARS[1];

export default function DailyCoach() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showGuides, setShowGuides] = useState(false);
  const [form, setForm] = useState({ pillar: "gratitude", category: "Self", energy: 3, text: "", share: false });
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["gauge-coach"],
    queryFn: () => api.get("/me/gauge/coach").then((r) => r.data),
    enabled: !!user,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.text.trim()) { toast.error("Write a few words to log your reflection."); return; }
    if (form.share && !data?.guide) { toast.error("Choose a guide first to share with them."); return; }
    setSaving(true);
    try {
      await api.post("/gauge/reflections", {
        pillar: form.pillar, category: form.category, energy: Number(form.energy),
        text: form.text.trim(), share_with: form.share ? "guide" : "none",
      });
      toast.success("Reflection logged — thank you for showing up for yourself");
      setForm((f) => ({ ...f, text: "", share: false }));
      qc.invalidateQueries({ queryKey: ["gauge-coach"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const applyPrompt = (p) => { setForm((f) => ({ ...f, pillar: p.pillar || "awareness", text: p.text || f.text })); document.getElementById("coach-textarea")?.focus(); };

  if (isLoading || !data) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={32} /></div>;
  }

  const stats = data.stats || {};
  const pMeta = pillarMeta(data.prompt?.pillar);
  const PIcon = pMeta.icon;

  return (
    <div data-testid="daily-coach" className="min-h-[70vh]">
      {/* Hero */}
      <section className="relative py-16 bg-[#144F75] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Compass className="mx-auto text-[#D98E73] mb-4" size={36} />
          <p className="eyw-label !text-[#D98E73] mb-2">GAUGE · Daily Coach</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight">Your daily reflection</h1>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">A gentle, private habit — one small check-in a day to notice how you're really doing.</p>
          <Link to="/gauge/journey" data-testid="coach-journey-link" className="inline-flex items-center gap-2 mt-5 eyw-btn-ghost !text-white !border-white/30 !py-2 text-sm">
            <LineChart size={15} /> View my GAUGE journey
          </Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        {/* Streak stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="coach-stats">
          <StatCard icon={Flame} label="Day streak" value={stats.streak ?? 0} accent="#D98E73" testid="coach-streak" />
          <StatCard icon={CheckCircle2} label="Reflections" value={stats.total ?? 0} accent="#5B8A72" testid="coach-total" />
          <StatCard icon={Zap} label="Avg energy" value={stats.avg_energy ?? 0} accent="#B5794E" testid="coach-energy" />
        </div>

        {/* Milestones */}
        {data.milestones?.next != null && (
          <div className="eyw-card p-5 flex items-center gap-3" data-testid="coach-milestone">
            <span className="grid place-items-center h-11 w-11 rounded-full bg-[#D98E73]/15 text-[#D98E73]"><Award size={20} /></span>
            <div className="flex-1">
              <p className="text-sm text-[#1A242B] font-medium">
                {stats.streak > 0 ? `${data.milestones.next - stats.streak} more day${data.milestones.next - stats.streak > 1 ? "s" : ""} to your ${data.milestones.next}-day milestone` : "Log today to begin your streak"}
              </p>
              {data.milestones.achieved?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {data.milestones.achieved.map((m) => (
                    <span key={m} className="text-xs bg-[#5B8A72]/12 text-[#5B8A72] px-2 py-0.5 rounded-full">{m}-day ✓</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Today's prompt */}
        <div className="eyw-card p-6 border-l-4" style={{ borderColor: pMeta.color }} data-testid="coach-prompt">
          <p className="eyw-label mb-1 flex items-center gap-1.5" style={{ color: pMeta.color }}><PIcon size={14} /> Today's {pMeta.label} prompt</p>
          <p className="font-serif text-2xl text-[#1A242B] leading-snug">{data.prompt?.text}</p>
        </div>

        {/* Guide's custom prompts */}
        {data.custom_prompts?.length > 0 && (
          <div className="space-y-2" data-testid="coach-guide-prompts">
            <p className="eyw-label flex items-center gap-1.5"><Sparkles size={12} /> From your guide {data.guide?.name ? `· ${data.guide.name}` : ""}</p>
            {data.custom_prompts.map((cp) => (
              <button key={cp.id} onClick={() => applyPrompt(cp)} data-testid={`coach-guide-prompt-${cp.id}`}
                className="w-full text-left eyw-card p-4 hover:-translate-y-0.5 transition-transform">
                <span className="text-xs uppercase tracking-wide" style={{ color: pillarMeta(cp.pillar).color }}>{pillarMeta(cp.pillar).label}</span>
                <p className="text-[#1A242B] mt-0.5">{cp.text}</p>
              </button>
            ))}
          </div>
        )}

        {/* Check-in form */}
        <div className="eyw-card p-6" data-testid="coach-checkin-form">
          {data.checked_in_today && (
            <p className="text-sm text-[#5B8A72] flex items-center gap-1.5 mb-4" data-testid="coach-checked-in"><CheckCircle2 size={15} /> You've checked in today — add another if you like.</p>
          )}
          <p className="eyw-label mb-2">How are you, really?</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {PILLARS.map((p) => {
              const Icon = p.icon; const active = form.pillar === p.key;
              return (
                <button key={p.key} onClick={() => set("pillar", p.key)} data-testid={`coach-pillar-${p.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${active ? "text-white border-transparent" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}
                  style={active ? { background: p.color } : {}}>
                  <Icon size={14} /> {p.label}
                </button>
              );
            })}
          </div>

          <textarea id="coach-textarea" rows={3} value={form.text} onChange={(e) => set("text", e.target.value)}
            data-testid="coach-textarea" placeholder="A sentence or two is plenty…" className="eyw-input w-full resize-none" />

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-xs text-[#4A5D6B] mb-1 block">Area of life</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="coach-category" className="eyw-input w-full">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#4A5D6B] mb-1 block">Energy: <span className="text-[#144F75] font-medium">{form.energy}/5</span></label>
              <input type="range" min="1" max="5" value={form.energy} onChange={(e) => set("energy", e.target.value)} data-testid="coach-energy-slider" className="w-full accent-[#144F75] mt-2" />
            </div>
          </div>

          {data.guide && (
            <label className="flex items-center justify-between text-sm text-[#1A242B] mt-4 bg-[#8FA89B]/[0.08] rounded-xl px-4 py-3">
              <span className="flex items-center gap-1.5"><UserCheck size={15} className="text-[#5B8A72]" /> Share this reflection privately with {data.guide.name}</span>
              <input type="checkbox" checked={form.share} onChange={(e) => set("share", e.target.checked)} data-testid="coach-share-guide" />
            </label>
          )}

          <button onClick={submit} disabled={saving} data-testid="coach-submit" className="eyw-btn w-full mt-5 disabled:opacity-60">
            {saving && <Loader2 className="animate-spin" size={17} />} Log today's reflection
          </button>
        </div>

        {/* Guide section */}
        <div className="eyw-card p-6" data-testid="coach-guide-section">
          <p className="eyw-label mb-2 flex items-center gap-1.5"><UserCheck size={13} /> Your guide</p>
          {data.guide ? (
            <div className="flex items-center gap-4">
              {data.guide.avatar ? <img src={data.guide.avatar} alt={data.guide.name} className="h-14 w-14 rounded-full object-cover" />
                : <span className="grid place-items-center h-14 w-14 rounded-full bg-[#144F75]/10 text-[#144F75]"><Users size={22} /></span>}
              <div className="flex-1">
                <h3 className="font-serif text-lg text-[#1A242B]">{data.guide.name}</h3>
                <p className="text-[#4A5D6B] text-sm">{data.guide.guide_bio || data.guide.headline}</p>
              </div>
              <button onClick={() => setShowGuides(true)} data-testid="coach-change-guide" className="eyw-btn-outline !py-2 text-sm">Change</button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <p className="text-[#4A5D6B] text-sm">Choose a certified guide to gently support your reflection journey — you can share entries privately with them.</p>
              <button onClick={() => setShowGuides(true)} data-testid="coach-choose-guide" className="eyw-btn !py-2 text-sm whitespace-nowrap">Choose a guide</button>
            </div>
          )}
        </div>

        {/* Certified guide link */}
        {data.is_certified_guide && (
          <Link to="/guide-coach" data-testid="coach-guide-dashboard-link" className="eyw-card p-5 flex items-center justify-between group border-l-4 border-[#144F75]">
            <div>
              <p className="eyw-label mb-0.5 flex items-center gap-1.5"><Sparkles size={12} /> You're a certified guide</p>
              <p className="text-[#1A242B]">Open your Guide dashboard to see members who chose you.</p>
            </div>
            <ArrowRight size={20} className="text-[#144F75] group-hover:translate-x-1 transition-transform" />
          </Link>
        )}

        {/* Recent reflections */}
        {data.recent?.length > 0 && (
          <div data-testid="coach-recent">
            <p className="eyw-label mb-3">Recent reflections</p>
            <div className="space-y-2">
              {data.recent.map((r) => {
                const m = pillarMeta(r.pillar);
                return (
                  <div key={r.id} className="eyw-card p-4 flex items-start gap-3">
                    <span className="grid place-items-center h-9 w-9 rounded-lg text-white text-sm shrink-0" style={{ background: m.color }}>{m.label[0]}</span>
                    <div className="flex-1">
                      <p className="text-[#1A242B] text-sm">{r.text}</p>
                      <p className="text-[#8FA89B] text-xs mt-0.5">{fmtDate(r.date)} · {r.category} · energy {r.energy}/5{r.share_with === "guide" ? " · shared with guide" : ""}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showGuides && <GuidePicker current={data.guide?.id} onClose={() => setShowGuides(false)}
        onChanged={() => { setForm((f) => ({ ...f, share: false })); qc.invalidateQueries({ queryKey: ["gauge-coach"] }); setShowGuides(false); }} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="eyw-card p-5 text-center" data-testid={testid}>
      <Icon size={20} className="mx-auto mb-1.5" style={{ color: accent }} />
      <p className="text-3xl font-serif text-[#1A242B]">{value}</p>
      <p className="text-xs text-[#4A5D6B] mt-0.5">{label}</p>
    </div>
  );
}

function GuidePicker({ current, onClose, onChanged }) {
  const { data: guides = [], isLoading } = useQuery({ queryKey: ["gauge-guides"], queryFn: () => api.get("/gauge/guides").then((r) => r.data) });
  const [busy, setBusy] = useState(false);

  const choose = async (id) => {
    setBusy(true);
    try { await api.post("/me/gauge/guide", { guide_id: id }); toast.success("Guide selected"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/me/gauge/guide"); toast.success("Guide removed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="coach-guide-picker">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <p className="eyw-label mb-1 flex items-center gap-1.5"><UserCheck size={14} /> Choose your guide</p>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-5">Certified guides</h3>
        {isLoading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#144F75]" size={26} /></div> : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {guides.length === 0 && <p className="text-sm text-[#4A5D6B]">No certified guides are available yet. Check back soon.</p>}
            {guides.map((g) => (
              <div key={g.id} data-testid={`guide-option-${g.id}`} className={`eyw-card p-4 flex items-center gap-3 ${current === g.id ? "border-2 border-[#144F75]" : ""}`}>
                {g.avatar ? <img src={g.avatar} alt={g.name} className="h-11 w-11 rounded-full object-cover" />
                  : <span className="grid place-items-center h-11 w-11 rounded-full bg-[#144F75]/10 text-[#144F75]"><Users size={18} /></span>}
                <div className="flex-1">
                  <h4 className="font-serif text-[#1A242B]">{g.name}</h4>
                  <p className="text-[#8FA89B] text-xs">{g.guide_bio || g.headline || g.location}</p>
                </div>
                <button onClick={() => choose(g.id)} disabled={busy || current === g.id} data-testid={`guide-choose-${g.id}`}
                  className="eyw-btn !py-1.5 text-xs disabled:opacity-50">{current === g.id ? "Current" : "Select"}</button>
              </div>
            ))}
          </div>
        )}
        {current && <button onClick={remove} disabled={busy} data-testid="guide-remove" className="text-red-500 text-sm mt-4 hover:underline">Remove my guide</button>}
      </div>
    </div>
  );
}
